/**
 * ConditionalEdgeModal - Modal for editing conditional edge logic
 * Reuses same shell layout as NodeConfigModal.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { parseConditionalFunction, validateConditionalFunction, generateConditionalTemplate } from '../utils/conditionalParser';
import './VariableInspector.css';
import './NodeConfigModal.css';
import './ConditionalEdgeModal.css';

const NodeInspector = ({ nodes, onInsertNode, selectedNode }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  return (
    <div className="variable-inspector">
      <div className="vi-body">
        <div className="vi-group">
          <div 
            className="vi-group-header"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className={`vi-expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
            <span className="vi-group-icon vi-icon-step">N</span>
            <span className="vi-group-title">Available Nodes</span>
            <span className="vi-group-count">{nodes.length}</span>
          </div>
          
          {isExpanded && (
            <div className="vi-group-content">
              {nodes.map(node => {
                const displayName = node.data?.label || node.label || node.id;
                const isCustomName = displayName !== node.id;
                return (
                  <div
                    key={node.id}
                    className={`vi-node-row vi-node-row--clickable ${selectedNode === node.id ? 'selected' : ''}`}
                    onClick={() => onInsertNode(node.id)}
                    title={isCustomName ? `${displayName} (${node.id})` : node.id}
                  >
                    <span className="vi-indent-guide vi-indent-guide--last" />
                    <span className="vi-expand-placeholder" />
                    <span className="vi-node-name">{displayName}</span>
                    <span 
                      className="vi-type-badge"
                      style={{ '--badge-color': '#ff9800' }}
                    >
                      node
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      <div className="vi-hint">
        Click to insert node ID
      </div>
    </div>
  );
};

export const ConditionalEdgeModal = ({
  isOpen,
  onClose,
  edgeData,
  availableNodes,
  edges = [],
  onSave
}) => {
  const [code, setCode] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      if (edgeData?.conditionalCode || edgeData?.data?.conditionalCode) {
        setCode(edgeData.conditionalCode || edgeData.data.conditionalCode);
      } else {
        // Get actual targets from edges coming OUT of this decision node
        const decisionNodeId = edgeData?.source;
        const outgoingEdges = edges.filter(e => e.source === decisionNodeId);
        const actualTargets = outgoingEdges.map(e => e.target);
        
        // Find the node that feeds INTO this decision node
        const incomingEdge = edges.find(e => e.target === decisionNodeId);
        const precedingNodeId = incomingEdge?.source || null;
        
        setCode(generateConditionalTemplate(actualTargets, decisionNodeId, precedingNodeId));
      }
    }
  }, [isOpen, edgeData, edges]);

  const handleInsertNode = useCallback((nodeId) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    setSelectedNode(nodeId);
    
    const textToInsert = `'${nodeId}'`;
    const selection = editor.getSelection();
    const id = { major: 1, minor: 1 };
    const op = { identifier: id, range: selection, text: textToInsert, forceMoveMarkers: true };
    editor.executeEdits('insert-node', [op]);
    editor.focus();
  }, []);

  const handleSave = () => {
    // Validate on save
    const validation = validateConditionalFunction(code);
    if (!validation.valid) {
      return;
    }
    
    const parsed = parseConditionalFunction(code);
    if (!parsed.success || parsed.outputs.length === 0) {
      return;
    }
    
    onSave({
      conditionalCode: code,
      discoveredOutputs: parsed.outputs,
      branches: parsed.branches
    });
    onClose();
  };

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monaco.editor.defineTheme('zibby-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: { 'editor.background': '#1e1e1e' }
    });
    monaco.editor.setTheme('zibby-dark');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="gwe-modal-backdrop" onClick={onClose} />
      <div className="gwe-modal gwe-modal--conditional">
        <div className="gwe-modal__header">
          <h2 className="gwe-modal__title">
            {edgeData?.source || 'Decision'} — Conditional Logic
          </h2>
          <div className="gwe-modal__actions">
            <button 
              type="button"
              className="gwe-modal__action-btn gwe-modal__action-btn--cancel" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="button"
              className="gwe-modal__action-btn gwe-modal__action-btn--save"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>

        <div className="gwe-modal__body">
          <div className="gwe-ide-content">
            <div className="gwe-prompt-section">
              {/* Left Sidebar - Available Nodes */}
              <div className="gwe-prompt-sidebar">
                <NodeInspector
                  nodes={availableNodes}
                  onInsertNode={handleInsertNode}
                  selectedNode={selectedNode}
                />
              </div>
              
              {/* Main Editor */}
              <div className="gwe-prompt-main">
                {/* Tab Bar */}
                <div className="gwe-editor-tabs">
                  <button className="gwe-editor-tab gwe-editor-tab--active">
                    <span>route.js</span>
                  </button>
                </div>

                {/* Editor Content */}
                <div className="gwe-editor-content" onKeyDown={(e) => e.stopPropagation()}>
                  <div className="gwe-tab-code">
                    <Editor
                      height="100%"
                      defaultLanguage="javascript"
                      value={code}
                      onChange={setCode}
                      onMount={handleEditorDidMount}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 13,
                        lineNumbers: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        formatOnPaste: true,
                        formatOnType: false,
                        wordWrap: 'on',
                        acceptSuggestionOnCommitCharacter: false,
                        acceptSuggestionOnEnter: 'on',
                        quickSuggestions: true,
                        suggestOnTriggerCharacters: true,
                        snippetSuggestions: 'none'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
